import PageBrand from '@components/common/PageBrand'
import Grid from '@mui/material/Grid'
import React, { useContext, useState } from 'react'
import { useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import BackgroundAnimation from '../components/common/BackgroundAnimation'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import { Link, useHistory } from 'react-router-dom'
import { isValidEmail, isValidPassword, passwordsMatch, sha256 } from '../utils/functions'
import { createAccount } from '../api/user'
import AppContext, { JWT } from '../app/AppContext'
import { jwtDecode } from 'jwt-decode'
import Alert, { AlertInput } from '../components/common/Alert'
import { GlassButton, GlassCard, GlassOutlineButton } from '@components/common/Glass'
import { setAuthToken } from '../utils/helpers/authStorage'

export default function CreateAccount(): JSX.Element {
  const theme = useTheme()
  const history = useHistory()
  const { appReducer } = useContext(AppContext)

  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [alert, setAlert] = useState<AlertInput>({ open: false })

  const [username, setUsername] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [passwordConfirm, setPasswordConfirm] = useState<string>('')
  const usernameMax = 20

  const inputSx = {
    '& .MuiInputBase-root': {
      background: '#ffffff',
      borderRadius: '14px',
      // border: '1px solid rgba(255,255,255,0.75)',
      boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
      backdropFilter: 'none',
    },
    '& .MuiInputBase-input': {
      py: 1.25,
    },
  } as const

  const handleSubmit = async () => {
    const [hashPass, hashCheckPass] = await Promise.all([sha256(password), sha256(passwordConfirm)])

    // check email and password again
    if (!email || !isValidEmail(email)) {
      setAlert({
        open: true,
        severity: 'error',
        message: 'Invalid email.',
      })
    } else if (!password || !isValidPassword(password)) {
      setAlert({
        open: true,
        severity: 'error',
        message:
          'Must contain an uppercase, lowercase, number, special character, and be at least 8 characters long.',
      })
    } else if (!password || !passwordConfirm || !passwordsMatch(password, passwordConfirm)) {
      setAlert({
        open: true,
        severity: 'error',
        message: 'Passwords do not match.',
      })
    }

    const result = await createAccount(email, username, hashPass, hashCheckPass)
    // On success create route to login page
    if (result.success) {
      const jwtData = jwtDecode(result.data || '') as JWT
      setAuthToken(result.data || '', jwtData)
      appReducer({ type: 'jwt', payload: jwtData })
      history.push('/')
    } else {
      setAlert({
        open: true,
        severity: 'error',
        message: result.message,
      })
    }
  }

  return (
    <div>
      <BackgroundAnimation />
      <img id="pencilImg" src="trophy.png" alt="" style={{ display: 'none' }} />
      <Container
        maxWidth="sm"
        sx={{
          minHeight: '100dvh',
          py: 3,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <PageBrand />
        <Stack justifyContent="center" alignItems="center" spacing={2}>
          <GlassCard
            sx={{
              p: { xs: 1.5, sm: 3.5 },
              borderRadius: '32px',
              borderColor: 'rgba(255,255,255,0.7)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0}>
              <GlassOutlineButton
                variant="outlined"
                onClick={() => history.push('/login')}
                sx={{
                  p: 1,
                  minWidth: 'auto',
                }}
              >
                <ArrowBackIosNewRoundedIcon fontSize="small" sx={{ mr: 0.25 }} />
              </GlassOutlineButton>
            </Box>

            <form
              onSubmit={async (e) => {
                e.preventDefault()
                await handleSubmit()
              }}
            >
              <Grid container justifyContent="center" spacing={1} mt={1}>
                <Grid item xs={12}>
                  <Typography variant={fullScreen ? 'h4' : 'h4'} fontWeight={600}>
                    Create Account
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    We <span style={{ textDecoration: 'underline' }}>only</span> use your email for
                    verification. <br /> No spam, marketing, data, or anything of the sort.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container justifyContent="center" spacing={2} mt={3}>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        fullWidth={true}
                        sx={inputSx}
                        label="Email"
                        name="email"
                        autoComplete="username"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required={true}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        fullWidth={true}
                        sx={inputSx}
                        label="Username"
                        name="display_name"
                        autoComplete="nickname"
                        value={username}
                        onChange={(e) => setUsername(e.target.value.slice(0, usernameMax))}
                        inputProps={{ maxLength: usernameMax }}
                        helperText={
                          username.length > usernameMax
                            ? 'Username must be 20 characters or fewer.'
                            : undefined
                        }
                        error={username.length > usernameMax}
                        required={true}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        fullWidth={true}
                        sx={inputSx}
                        label="Password"
                        type="password"
                        autoComplete="new-password"
                        name="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required={true}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        fullWidth={true}
                        sx={inputSx}
                        placeholder="Confirm password"
                        label="Confirm password"
                        type="password"
                        autoComplete="new-password"
                        name="new-password-confirm"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        required={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} mt={2}>
                  <GlassButton
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                      minHeight: 48,
                      fontSize: 16,
                    }}
                    type="submit"
                  >
                    Create Account
                  </GlassButton>
                </Grid>
              </Grid>
            </form>
          </GlassCard>

          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <Link
              to="/privacy"
              style={{
                textDecoration: 'none',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#60768b' }}>
                Privacy Policy
              </Typography>
            </Link>
            <Link
              to="/terms"
              style={{
                textDecoration: 'none',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#60768b' }}>
                Terms of Service
              </Typography>
            </Link>
          </Box>
        </Stack>
      </Container>
      <Alert alert={alert} onClose={() => setAlert({ ...alert, open: false })} />
    </div>
  )
}
